import { useEffect, useState } from "react";
import { Phone, Mail, Globe, FileDown, Briefcase, Facebook, Instagram, Linkedin, MapPin, Calculator, Image as ImageIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useContactInfo } from "@/context/SiteContentContext";
import { BrandMark } from "./BrandMark";
import Newsletter from "./Newsletter";
import LocationMap from "./LocationMap";
import { fetchSettings, toAbsolute } from "@/lib/api";

// TikTok icon (not shipped in lucide-react)
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.68a8.16 8.16 0 0 0 4.77 1.52V6.75a4.85 4.85 0 0 1-1.84-.06Z"/>
  </svg>
);

export default function Footer() {
  const { t } = useLang();
  const CONTACT_INFO = useContactInfo();
  const year = new Date().getFullYear();
  const [pdf, setPdf] = useState("");

  useEffect(() => {
    fetchSettings().then((r) => setPdf(r.data?.profile_pdf || "")).catch(() => {});
  }, []);

  return (
    <footer className="relative border-t border-white/10 overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 radial-fade opacity-60" />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start mb-12">
          <div className="flex flex-col gap-5">
            <BrandMark size={72} />
            <h3 className="text-2xl sm:text-3xl font-extrabold glow-text max-w-md">{t.footer.slogan}</h3>
            <div className="flex flex-col gap-3 mt-2">
              <a href={`tel:${CONTACT_INFO.phone}`} className="flex items-center gap-2 text-slate-300 hover:text-primary transition-colors w-fit" data-testid="footer-phone">
                <Phone size={18} /> <span dir="ltr">{CONTACT_INFO.phone}</span>
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 text-slate-300 hover:text-primary transition-colors w-fit" data-testid="footer-email">
                <Mail size={18} /> {CONTACT_INFO.email}
              </a>
              <span className="flex items-center gap-2 text-slate-300"><Globe size={18} /> {CONTACT_INFO.website}</span>
              <span className="flex items-start gap-2 text-slate-300 text-sm leading-relaxed max-w-md">
                <MapPin size={18} className="shrink-0 mt-0.5" />
                <span>{CONTACT_INFO.address}</span>
              </span>
            </div>
            <div className="flex flex-wrap gap-4 mt-2">
              <Link to="/careers" className="flex items-center gap-2 text-sm text-slate-300 hover:text-primary transition-colors" data-testid="footer-careers">
                <Briefcase size={16} /> {t.careers.tag}
              </Link>
              <Link to="/roi-calculator" className="flex items-center gap-2 text-sm text-slate-300 hover:text-primary transition-colors" data-testid="footer-roi">
                <Calculator size={16} /> ROI Calculator
              </Link>
              <Link to="/resources" className="flex items-center gap-2 text-sm text-slate-300 hover:text-primary transition-colors" data-testid="footer-resources">
                <FileDown size={16} /> Resources
              </Link>
              <Link to="/gallery" className="flex items-center gap-2 text-sm text-slate-300 hover:text-primary transition-colors" data-testid="footer-gallery">
                <ImageIcon size={16} /> {t.nav.gallery}
              </Link>
              {pdf && (
                <a href={toAbsolute(pdf)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-300 hover:text-primary transition-colors" data-testid="footer-profile">
                  <FileDown size={16} /> {t.profile}
                </a>
              )}
            </div>
            {/* Social icons */}
            <div className="flex items-center gap-3 mt-3" data-testid="footer-social">
              {[
                { key: "facebook", href: CONTACT_INFO.social.facebook, Icon: Facebook, color: "#1877F2" },
                { key: "instagram", href: CONTACT_INFO.social.instagram, Icon: Instagram, color: "#E1306C" },
                { key: "linkedin", href: CONTACT_INFO.social.linkedin, Icon: Linkedin, color: "#0A66C2" },
                { key: "tiktok", href: CONTACT_INFO.social.tiktok, Icon: TikTokIcon, color: "#000000" },
              ].map(({ key, href, Icon, color }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-10 w-10 rounded-full grid place-items-center border border-white/10 bg-white/5 text-slate-300 hover:text-white transition-colors"
                  onMouseEnter={(e) => { e.currentTarget.style.background = color; e.currentTarget.style.borderColor = color; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
                  aria-label={key}
                  data-testid={`footer-social-${key}`}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:justify-self-end w-full max-w-md space-y-5">
            <Newsletter />
            <LocationMap variant="mini" />
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <span>© {year} {t.footer.built}</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-primary transition-colors" data-testid="footer-privacy">{t.legal.privacy}</Link>
            <Link to="/terms" className="hover:text-primary transition-colors" data-testid="footer-terms">{t.legal.terms}</Link>
            <span>{t.footer.rights}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
